import { ACTIVITY_KEY } from "../../utils/sessionConfig";
import { computeSessionState } from "./sessionState";

const ACTIVITY_EVENTS = [
    "mousedown",
    "mousemove",
    "keydown",
    "scroll",
    "wheel",
    "touchstart",
    "pointerdown",
    "input",
    "change",
];
const ACTIVITY_WRITE_THROTTLE_MS = 1000;
const TICK_MS = 1000;

export class SessionTimer {
    constructor({ idleLimitMs, warnBeforeMs, onChange, onExpire }) {
        this.idleLimitMs = idleLimitMs;
        this.warnBeforeMs = warnBeforeMs;
        this.onChange = onChange || (() => {});
        this.onExpire = onExpire || (() => {});
        this._interval = null;
        this._lastWrite = 0;
        this._expired = false;
        this._channel = null;
        this._onActivity = this._onActivity.bind(this);
        this._onStorage = this._onStorage.bind(this);
        this._onVisibility = this._onVisibility.bind(this);
        this._onMessage = this._onMessage.bind(this);
    }

    _now() {
        return Date.now();
    }

    _getLastActivity() {
        const raw = window.localStorage.getItem(ACTIVITY_KEY);
        if (raw === null) return this._now();
        const v = parseInt(raw, 10);
        return Number.isFinite(v) ? v : this._now();
    }

    _writeActivity(broadcast) {
        const now = this._now();
        window.localStorage.setItem(ACTIVITY_KEY, String(now));
        this._lastWrite = now;
        if (broadcast && this._channel) {
            try {
                this._channel.postMessage({ type: "activity", at: now });
            } catch (_) {}
        }
    }

    _emit() {
        if (this._expired) return;
        const state = computeSessionState({
            now: this._now(),
            lastActivityAt: this._getLastActivity(),
            idleLimitMs: this.idleLimitMs,
            warnBeforeMs: this.warnBeforeMs,
        });
        if (state.phase === "expired") {
            this._expired = true;
            this.onExpire();
            return;
        }
        this.onChange(state);
    }

    _onActivity() {
        // Typing into the recovery prompt must never revive an expired session.
        if (this._expired || this._interval === null) return;
        const now = this._now();
        if (now - this._lastWrite >= ACTIVITY_WRITE_THROTTLE_MS) {
            this._writeActivity(true);
        }
        this._emit();
    }

    _onStorage(e) {
        if (e.key === ACTIVITY_KEY && e.newValue !== null) this._emit();
    }

    _onMessage(e) {
        if (e && e.data && e.data.type === "activity") this._emit();
    }

    _onVisibility() {
        if (document.visibilityState === "visible") this._emit();
    }

    stayActive() {
        if (this._expired || this._interval === null) return;
        this._writeActivity(true);
        this._emit();
    }

    start() {
        if (this._interval !== null) return;
        this._expired = false;
        // Measure idle from when monitoring begins, not a possibly-stale login timestamp.
        this._writeActivity(false);
        if (typeof BroadcastChannel !== "undefined") {
            try {
                this._channel = new BroadcastChannel("rdc-auth");
                this._channel.addEventListener("message", this._onMessage);
            } catch (_) {
                this._channel = null;
            }
        }
        ACTIVITY_EVENTS.forEach((ev) =>
            // Editors and dropdowns often stop propagation. Capture real activity
            // before their handlers so actively filling a form cannot look idle.
            window.addEventListener(ev, this._onActivity, { passive: true, capture: true })
        );
        window.addEventListener("storage", this._onStorage);
        document.addEventListener("visibilitychange", this._onVisibility);
        this._interval = setInterval(() => this._emit(), TICK_MS);
        this._emit();
    }

    destroy() {
        if (this._interval !== null) clearInterval(this._interval);
        this._interval = null;
        ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, this._onActivity, true));
        window.removeEventListener("storage", this._onStorage);
        document.removeEventListener("visibilitychange", this._onVisibility);
        if (this._channel) {
            try {
                this._channel.removeEventListener("message", this._onMessage);
                this._channel.close();
            } catch (_) {}
            this._channel = null;
        }
    }
}
